import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Users, Clock, Radio, CheckCircle } from 'lucide-react';
import { formatDuration, formatPrice } from '../../lib/utils';

interface CourseCardProps {
  course: {
    id: string;
    slug: string;
    title: string;
    shortDescription?: string;
    thumbnailUrl?: string;
    price: number;
    currency?: string;
    level: string;
    rating?: number;
    totalReviews: number;
    totalStudents: number;
    totalDuration?: number;
    courseType?: 'RECORDED' | 'LIVE';
    isEnrolled?: boolean; // From protected browse endpoint
    professor: {
      firstName: string;
      lastName: string;
      avatarUrl?: string;
    };
    category?: {
      name: string;
    };
  };
  index?: number;
}

export default function CourseCard({ course, index = 0 }: CourseCardProps) {
  const professorName = `${course.professor.firstName} ${course.professor.lastName}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.15), duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/courses/${course.slug}`}
        className="card card-hover block group h-full"
      >
        {/* Thumbnail */}
        <div className="relative aspect-video rounded-xl overflow-hidden -mt-1 -mx-1 mb-4 bg-void-400">
          {course.thumbnailUrl ? (
            <img
              src={course.thumbnailUrl}
              alt={course.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center"
                 style={{ background: 'linear-gradient(145deg, rgba(42, 74, 91, 0.3) 0%, rgba(15, 16, 20, 0.8) 100%)' }}>
              <div className="text-slate-light/60 font-semibold text-2xl">
                {course.title.charAt(0)}
              </div>
            </div>
          )}
          
          {/* Price badge or Enrolled badge */}
          {course.isEnrolled ? (
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg 
                          bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/30
                          font-semibold text-sm text-emerald-400 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" />
              Enrolled
            </div>
          ) : (
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg 
                          bg-void/80 backdrop-blur-sm border border-white/10
                          font-semibold text-sm text-white">
              {formatPrice(course.price, course.currency)}
            </div>
          )}
          
          {/* Badges row - Level and Course Type */}
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="badge text-[10px] bg-void/80 backdrop-blur-sm text-white border border-white/20 font-semibold">
              {course.level}
            </span>
            {course.courseType === 'LIVE' && (
              <span className="badge text-[10px] bg-purple-500/80 backdrop-blur-sm text-white border border-purple-400/30 font-semibold flex items-center gap-1">
                <Radio className="w-3 h-3" />
                LIVE
              </span>
            )}
          </div>
        </div>

        {/* Category */}
        {course.category && (
          <span className="text-slate-light text-[10px] font-semibold uppercase tracking-wider">
            {course.category.name}
          </span>
        )}

        {/* Title */}
        <h3 className="mt-2 font-semibold text-white leading-tight line-clamp-2 
                     group-hover:text-slate-lighter transition-colors duration-200">
          {course.title}
        </h3>

        {/* Description */}
        {course.shortDescription && (
          <p className="mt-2 text-white/40 text-sm line-clamp-2 leading-relaxed">
            {course.shortDescription}
          </p>
        )}

        {/* Professor */}
        <div className="mt-4 flex items-center gap-2.5">
          {course.professor.avatarUrl ? (
            <img
              src={course.professor.avatarUrl}
              alt={professorName}
              className="w-7 h-7 rounded-full object-cover ring-2 ring-white/10"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-slate/20 flex items-center justify-center 
                          text-slate-light text-xs font-medium">
              {course.professor.firstName.charAt(0)}
            </div>
          )}
          <span className="text-white/50 text-sm truncate">{professorName}</span>
        </div>

        {/* Stats */}
        <div className="mt-4 pt-4 border-t border-white/[0.06] flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
          {/* Rating */}
          <div className="flex items-center gap-1">
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            <span className="text-white font-medium">
              {Number(course.rating || 0).toFixed(1)}
            </span>
            <span className="text-white/40">
              ({(course.totalReviews || 0).toLocaleString()})
            </span>
          </div>
          
          {/* Students */}
          <div className="flex items-center gap-1 text-white/40">
            <Users className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{course.totalStudents.toLocaleString()}</span>
          </div>
          
          {/* Duration or Live Sessions */}
          {course.courseType === 'LIVE' ? (
            <div className="flex items-center gap-1 text-purple-400">
              <Radio className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Live</span>
            </div>
          ) : course.totalDuration ? (
            <div className="flex items-center gap-1 text-white/40">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{formatDuration(course.totalDuration)}</span>
            </div>
          ) : null}
        </div>
      </Link>
    </motion.div>
  );
}
